import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { useUser } from "../../contexts/UserContext";
import { fetchJson } from "../../utils/fetchJson";
import useScopeAutoRefresh from "../../hooks/useScopeAutoRefresh";
import { getCurrentAcademicYearId } from '../../utils/academicYearContext';

const buildEvaluationLabel = (evaluation) => {
  if (!evaluation) return '';
  if (evaluation.name) return evaluation.name;
  if (evaluation.type === 'sequence' && evaluation.sequenceNumber) return `Sequence ${evaluation.sequenceNumber}`;
  if (evaluation.type === 'term' && evaluation.termNumber) return `Term ${evaluation.termNumber}`;
  return `Evaluation ${evaluation.id || ''}`.trim();
};

const formatSubjectLevel = (level) => {
  const normalized = String(level || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'first_cycle') return 'First Cycle';
  if (normalized === 'second_cycle') return 'Second Cycle';
  return normalized.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const toFiniteNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const formatNumber = (value, digits = 2) => {
  const numeric = toFiniteNumber(value);
  return numeric == null ? '—' : numeric.toFixed(digits);
};

const formatPercent = (value) => {
  const numeric = toFiniteNumber(value);
  return numeric == null ? '—' : `${numeric.toFixed(1)}%`;
};

const normalizeAcademicYear = (year) => {
  if (!year) return null;
  return {
    ...year,
    startDate: year.startDate || year.startsAt || null,
    endDate: year.endDate || year.endsAt || null,
  };
};

const buildAcademicYearMonths = (startDate, endDate) => {
  if (!startDate || !endDate) return [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const endCursor = new Date(end.getFullYear(), end.getMonth(), 1);
  const items = [];
  while (cursor <= endCursor) {
    const month = cursor.getMonth() + 1;
    const year = cursor.getFullYear();
    items.push({
      key: `${year}-${String(month).padStart(2, '0')}`,
      month,
      year,
      label: cursor.toLocaleString(undefined, { month: 'long', year: 'numeric' })
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return items;
};

const formatReportPeriod = (month, year) => {
  const numericMonth = Number(month);
  const numericYear = Number(year);
  if (Number.isFinite(numericMonth) && numericMonth >= 1 && numericMonth <= 12) {
    try {
      return new Date(Number.isFinite(numericYear) ? numericYear : 2000, numericMonth - 1, 1).toLocaleString(undefined, {
        month: 'long',
        year: Number.isFinite(numericYear) ? 'numeric' : undefined,
      });
    } catch (_) {}
  }
  return [month, year].filter(Boolean).join(' / ') || '—';
};

/**
 * One implementation, two pages.
 *
 * @param {object} props
 * @param {'performance'|'reports'} props.mode which page this is
 */
export default function SubjectReportPage({ mode }) {
  const { t } = useTranslation();
  const user = useUser();
  const { pathname } = useLocation();

  // TWO PAGES, NOT TWO TABS.
  //
  // Subject performance and the departmental report are unrelated jobs: one is
  // marks analysis, the other is filing monthly paperwork. They shared a tab
  // strip here, which meant each menu entry opened a page showing the other
  // half's tab as well — and the phone has never done this. There the two are
  // separate screens (SubjectPerformanceScreen, DepartmentalReportScreen), and
  // this brings the web into line.
  //
  // `mode` is passed by the route so the page is explicit about which one it
  // is; the path is only a fallback for any caller that renders it directly.
  const isPerformance = mode
    ? mode === 'performance'
    : !pathname.includes('subject-report');
  const [subjectsRaw, setSubjectsRaw] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [classes, setClasses] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [selectedEval, setSelectedEval] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Kept apart from `error` on purpose: one is a fault, the other is the server
  // explaining the scope of this page. They read very differently to a user.
  const [notice, setNotice] = useState('');
  const [passAverage, setPassAverage] = useState(10);
  const [currentAcademicYear, setCurrentAcademicYear] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [uploadsLoading, setUploadsLoading] = useState(false);
  const [uploadsError, setUploadsError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadMonth, setUploadMonth] = useState('');
  const [uploadYear, setUploadYear] = useState('');
  const [uploadFile, setUploadFile] = useState(null);

  // THE YEAR SWITCHER HAS TO REACH THIS PAGE.
  //
  // A head of department looks back: last year's subject results are how you
  // tell whether this year's are any good. The switcher sat in the top bar and
  // this page ignored it entirely — six loaders, every one of them run once on
  // mount — so choosing a finished year left last session's figures on screen
  // under the new year's heading. Every number was then wrong in the most
  // expensive way available: quietly, and with a plausible label above it.
  //
  // ONE NONCE rather than six useCallbacks. These loaders depend on each other
  // through selectedSubjectId, and rewriting them into callbacks to re-invoke
  // individually is a large change to a page that only needs to start again
  // from the top. Bumping this re-runs the lot, in their existing order.
  const [scopeNonce, setScopeNonce] = useState(0);
  useScopeAutoRefresh([() => setScopeNonce((n) => n + 1)]);

  const headedDepartments = useMemo(() => (Array.isArray(user?.departmentHeadOf) ? user.departmentHeadOf : []), [user]);
  const headedDepartmentIds = useMemo(() => new Set(headedDepartments.map((dept) => String(dept?.id || '')).filter(Boolean)), [headedDepartments]);
  const departmentNameById = useMemo(() => new Map(headedDepartments.map((dept) => [String(dept?.id || ''), dept?.name || ''])), [headedDepartments]);

  const subjectOptions = useMemo(() => {
    const out = [];
    (Array.isArray(subjectsRaw) ? subjectsRaw : []).forEach((item) => {
      const departmentId = item?.departmentId ? String(item.departmentId) : '';
      // THE NAME OFF THE ROW FIRST, the lookup second.
      //
      // departmentNameById is built from user.departmentHeadOf, which the server
      // scopes by phase — so when that scoping misses, the map is empty and
      // every subject on this page loses its department name even though the
      // subject rows themselves carry one. The page then shows figures with no
      // indication of whose department they are.
      const departmentName = String(
        item?.department?.name || item?.departmentName || departmentNameById.get(departmentId) || ''
      ).trim();
      const variants = Array.isArray(item?.variants) ? item.variants : [];
      const pushSubject = (subjectLike, fallbackGroup = null) => {
        const subjectId = subjectLike?.id ? String(subjectLike.id) : '';
        const subjectName = String(subjectLike?.name || fallbackGroup?.name || '').trim();
        const level = String(subjectLike?.level || '').trim().toLowerCase();
        if (!subjectId || !subjectName) return;
        const levelLabel = formatSubjectLevel(level);
        out.push({
          id: subjectId,
          name: subjectName,
          subjectName,
          level,
          levelLabel,
          departmentId,
          departmentName,
          subjectGroupId: subjectLike?.subjectGroupId ? String(subjectLike.subjectGroupId) : (fallbackGroup?.id ? String(fallbackGroup.id) : ''),
          isDepartmentHeadScope: Boolean(departmentId && headedDepartmentIds.has(departmentId))
        });
      };
      if (variants.length) {
        variants.forEach((variant) => pushSubject(variant, item));
        return;
      }
      if (item?.level || item?.subjectGroupId) pushSubject(item, item);
    });
    return out.filter((item) => item.id);
  }, [departmentNameById, headedDepartmentIds, subjectsRaw]);

  const selectedSubjectMeta = useMemo(() => subjectOptions.find((item) => String(item.id) === String(selectedSubjectId)) || null, [selectedSubjectId, subjectOptions]);
  const selectedEvaluationMeta = useMemo(() => evaluations.find((item) => String(item.id) === String(selectedEval)) || null, [evaluations, selectedEval]);
  // THE SELECTED SUBJECT'S DEPARTMENT FIRST, then the only one this person
  // heads. Taking headedDepartments[0] first named the wrong department for
  // anybody who runs more than one — it answered "which department do you head"
  // when the question is "which department is this page showing".
  const activeDepartment = useMemo(() => {
    if (selectedSubjectMeta?.departmentId || selectedSubjectMeta?.departmentName) {
      return { id: selectedSubjectMeta.departmentId || '', name: selectedSubjectMeta.departmentName || '' };
    }
    if (headedDepartments.length === 1) return headedDepartments[0];
    // Nothing selected yet: if every subject on offer belongs to one
    // department, that is the department this page is about. Taken from the
    // subject rows rather than from user.departmentHeadOf, which is empty
    // whenever the server's phase scoping misses.
    const names = [...new Set(subjectOptions.map((o) => o.departmentName).filter(Boolean))];
    if (names.length === 1) return { id: '', name: names[0] };
    return null;
  }, [headedDepartments, selectedSubjectMeta, subjectOptions]);
  const monthOptions = useMemo(() => buildAcademicYearMonths(currentAcademicYear?.startDate, currentAcademicYear?.endDate), [currentAcademicYear]);
  const yearOptions = useMemo(() => Array.from(new Set(monthOptions.map((item) => String(item.year)))), [monthOptions]);
  const selectedMonthOptionKey = useMemo(() => {
    return monthOptions.find((item) => String(item.month) === String(uploadMonth) && String(item.year) === String(uploadYear))?.key || '';
  }, [monthOptions, uploadMonth, uploadYear]);
  const classPerformanceRows = useMemo(() => {
    const grouped = new Map();
    rows.forEach((row) => {
      const classId = String(row.classId || row.className || 'unknown');
      const className = row.className || t('subjectReportPage.labels.unknownClass');
      const score = toFiniteNumber(row.score);
      if (score == null) return;

      if (!grouped.has(classId)) {
        grouped.set(classId, {
          classId,
          className,
          students: 0,
          passed: 0,
          totalScore: 0,
          minScore: null,
          maxScore: null,
        });
      }

      const current = grouped.get(classId);
      current.students += 1;
      current.totalScore += score;
      if (score >= Number(passAverage || 10)) current.passed += 1;
      current.minScore = current.minScore == null ? score : Math.min(current.minScore, score);
      current.maxScore = current.maxScore == null ? score : Math.max(current.maxScore, score);
    });

    return Array.from(grouped.values())
      .map((item) => ({
        ...item,
        averageScore: item.students > 0 ? item.totalScore / item.students : null,
        successRate: item.students > 0 ? (item.passed / item.students) * 100 : 0,
      }))
      .sort((a, b) => a.className.localeCompare(b.className));
  }, [passAverage, rows, t]);
  const performanceSummary = useMemo(() => {
    const scores = rows.map((row) => toFiniteNumber(row.score)).filter((score) => score != null);
    const total = scores.reduce((sum, score) => sum + score, 0);
    const passed = scores.filter((score) => score >= Number(passAverage || 10)).length;
    return {
      classes: classPerformanceRows.length,
      students: scores.length,
      average: scores.length ? total / scores.length : null,
      successRate: scores.length ? (passed / scores.length) * 100 : 0,
    };
  }, [classPerformanceRows.length, passAverage, rows]);

  useEffect(() => {
    async function loadSubjects() {
      try {
        setError('');
        setNotice('');
        const data = await fetchJson('/api/subject-head/subjects');
        setSubjectsRaw(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setSubjectsRaw([]);
        // A REFUSAL IS NOT A FAILURE. The endpoint answers 403 to say this
        // account has no head-of-department scope in the phase it is working
        // in — a statement about the session, not a broken page. Reporting it
        // in red as "Failed to load subjects" sent people looking for a fault
        // that was not there, and hid the one sentence that explained it.
        const status = e?.status ?? e?.response?.status;
        // e.data.error BEFORE e.userMessage: fetchJson maps every 403 to the
        // same generic "You do not have permission to perform this action",
        // which is exactly the sentence that leaves somebody stuck. The server
        // says WHICH scope is missing, and that is the useful half.
        const serverMessage = String(e?.data?.error || e?.userMessage || e?.message || '').trim();
        if (status === 403) {
          setNotice(serverMessage || t('subjectReportPage.messages.secondaryOnly', {
            defaultValue: 'Subject performance is only available for the secondary phase.',
          }));
        } else {
          setError(serverMessage || t('subjectPerformanceMatrixPage.errors.loadSubjects'));
        }
      }
    }
    loadSubjects();
  }, [t, scopeNonce]);

  useEffect(() => {
    async function loadCurrentAcademicYear() {
      try {
        const data = await fetchJson('/api/global-academic-years');
        const list = (Array.isArray(data) ? data : []).map(normalizeAcademicYear);
        const currentAyId = String(getCurrentAcademicYearId() || '');
        const resolved = list.find((item) => String(item?.id || '') === currentAyId) || list.find((item) => item?.isCurrent) || list[0] || null;
        setCurrentAcademicYear(resolved);
      } catch (_) {
        setCurrentAcademicYear(null);
      }
    }
    loadCurrentAcademicYear();
  }, [scopeNonce]);

  useEffect(() => {
    let active = true;
    async function loadPassAverage() {
      try {
        const resp = await fetchJson('/school-setup-and-configuration/school-settings', { method: 'GET' });
        if (!active) return;
        const school = (resp && (resp.data?.school || resp.school || resp.data || resp)) || {};
        const numeric = Number(school?.passAverage);
        if (Number.isFinite(numeric) && numeric >= 0) setPassAverage(numeric);
      } catch (_) {}
    }
    loadPassAverage();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!monthOptions.length) return;
    const preferred = monthOptions.find((item) => {
      const now = new Date();
      return item.month === (now.getMonth() + 1) && item.year === now.getFullYear();
    }) || monthOptions[0];
    if (!monthOptions.some((item) => String(item.month) === String(uploadMonth) && String(item.year) === String(uploadYear))) {
      setUploadMonth(String(preferred.month));
      setUploadYear(String(preferred.year));
    }
  }, [monthOptions, uploadMonth, uploadYear]);

  useEffect(() => {
    async function loadClasses() {
      setClasses([]);
      if (!selectedSubjectId) return;
      try {
        const data = await fetchJson(`/api/subject-head/classes?subjectId=${encodeURIComponent(selectedSubjectId)}`);
        const list = Array.isArray(data) ? data : [];
        setClasses(list);
      } catch (e) {
        console.error(e);
        setError(t('subjectReportPage.messages.loadClassesFailed'));
      }
    }
    loadClasses();
  }, [selectedSubjectId, t, scopeNonce]);

  useEffect(() => {
    async function loadEvaluations() {
      setEvaluations([]);
      setSelectedEval('');
      if (!selectedSubjectId) return;
      try {
        const data = await fetchJson(`/api/subject-head/evaluations?subjectId=${encodeURIComponent(selectedSubjectId)}`);
        const list = Array.isArray(data) ? data : [];
        setEvaluations(list);
        if (list.length) setSelectedEval(String(list[0].id));
      } catch (e) {
        console.error(e);
        setError(t('subjectPerformanceMatrixPage.errors.loadEvaluations'));
      }
    }
    loadEvaluations();
  }, [selectedSubjectId, t, scopeNonce]);

  useEffect(() => {
    async function loadPerformance() {
      setRows([]);
      if (!isPerformance || !selectedSubjectId || !selectedEval) return;
      setLoading(true);
      setError('');
      try {
        const termId = Number(selectedEvaluationMeta?.termNumber) > 0 ? Number(selectedEvaluationMeta.termNumber) : 1;
        const data = await fetchJson(`/api/subject-head/performance/subject/${encodeURIComponent(selectedSubjectId)}/${encodeURIComponent(termId)}/${encodeURIComponent(selectedEval)}`);
        const computed = (Array.isArray(data) ? data : []).map((mark) => {
          const student = mark?.Enrollment?.Student || {};
          const cls = mark?.Enrollment?.Class || {};
          const fullName = [student.firstName, student.lastName].filter(Boolean).join(' ').trim() || student.fullName || student.name || `Student ${mark?.studentId || mark?.Enrollment?.studentId || ''}`.trim();
          return {
            id: String(mark?.id || `${fullName}-${mark?.Enrollment?.id || ''}`),
            classId: mark?.Enrollment?.classId || cls?.id || null,
            className: cls?.name || `Class ${mark?.Enrollment?.classId || cls?.id || ''}`.trim(),
            studentName: fullName,
            score: Number(mark?.score) || 0,
          };
        }).sort((a, b) => a.className.localeCompare(b.className) || b.score - a.score || a.studentName.localeCompare(b.studentName));
        setRows(computed);
      } catch (e) {
        console.error(e);
        setError(t('subjectPerformanceMatrixPage.errors.loadMarks'));
      } finally {
        setLoading(false);
      }
    }
    loadPerformance();
  }, [selectedEval, selectedEvaluationMeta, selectedSubjectId, isPerformance, t, scopeNonce]);

  const loadUploads = async () => {
    if (!activeDepartment?.id) return;
    setUploadsLoading(true);
    setUploadsError('');
    try {
      const data = await fetchJson(`/api/subject-head/monthly-reports?departmentId=${encodeURIComponent(activeDepartment.id)}`);
      setUploads(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setUploadsError(t('subjectReportPage.messages.loadUploadsFailed'));
    } finally {
      setUploadsLoading(false);
    }
  };

  useEffect(() => {
    if (isPerformance) return;
    setUploads([]);
    if (!activeDepartment?.id) return;
    loadUploads();
  }, [activeDepartment?.id, isPerformance]);

  const handleUpload = async () => {
    const monthNum = Number(String(uploadMonth || '').trim());
    const yearNum = Number(String(uploadYear || '').trim());
    if (!activeDepartment?.id) {
      setUploadsError(t('subjectReportPage.messages.noDepartmentScope'));
      return;
    }
    if (!uploadFile) {
      setUploadsError(t('subjectReportPage.messages.chooseFileRequired'));
      return;
    }
    if (!Number.isFinite(monthNum) || monthNum < 1 || monthNum > 12) {
      setUploadsError(t('subjectReportPage.messages.invalidMonth'));
      return;
    }
    if (!Number.isFinite(yearNum) || yearNum < 2000 || yearNum > 2100) {
      setUploadsError(t('subjectReportPage.messages.invalidYear'));
      return;
    }
    setUploading(true);
    setUploadsError('');
    try {
      const form = new FormData();
      form.append('departmentId', String(activeDepartment.id));
      form.append('month', String(monthNum));
      form.append('year', String(yearNum));
      form.append('file', uploadFile);
      await fetchJson('/api/subject-head/monthly-reports', { method: 'POST', body: form });
      setUploadFile(null);
      await loadUploads();
    } catch (e) {
      console.error(e);
      setUploadsError(e?.userMessage || e?.message || t('subjectReportPage.messages.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteUpload = async (id) => {
    if (!id) return;
    setUploadsLoading(true);
    setUploadsError('');
    try {
      await fetchJson(`/api/subject-head/monthly-reports/${id}`, { method: 'DELETE' });
      await loadUploads();
    } catch (e) {
      console.error(e);
      setUploadsError(e?.userMessage || e?.message || t('subjectReportPage.messages.deleteFailed'));
    } finally {
      setUploadsLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 4 } }}>
      {/* The page names itself. With the tab strip gone there is nothing else
          on screen to say which of the two this is. */}
      <Typography variant="h4" gutterBottom>
        {isPerformance
          ? t('subjectReportPage.tabs.performance')
          : t('subjectReportPage.tabs.reports')}
      </Typography>

      {/* WHICH DEPARTMENT THIS PAGE IS ABOUT, said once, at the top — the same
          as the phone. The subject list below is already scoped to it, so
          without this the page shows a department's figures without ever naming
          the department. */}
      {Boolean(activeDepartment?.name) && (
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
          {activeDepartment.name}
        </Typography>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && !error && <Alert severity="info" sx={{ mb: 2 }}>{notice}</Alert>}

      {isPerformance && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="department-subject-select-label">{t('subjectReportPage.fields.subject', 'Subject')}</InputLabel>
                <Select
                  labelId="department-subject-select-label"
                  value={selectedSubjectId}
                  label={t('subjectReportPage.fields.subject', 'Subject')}
                  onChange={(event) => setSelectedSubjectId(String(event.target.value || ''))}
                >
                  {subjectOptions.map((subject) => (
                    <MenuItem key={subject.id} value={subject.id}>{subject.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={!selectedSubjectId}>
                <InputLabel id="department-evaluation-select-label">{t('subjectReportPage.fields.evaluation')}</InputLabel>
                <Select
                  labelId="department-evaluation-select-label"
                  value={selectedEval}
                  label={t('subjectReportPage.fields.evaluation')}
                  onChange={(event) => setSelectedEval(String(event.target.value || ''))}
                >
                  {evaluations.map((evaluation) => (
                    <MenuItem key={evaluation.id} value={String(evaluation.id)}>{buildEvaluationLabel(evaluation)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* A DISABLED PICKER WITH NOTHING BESIDE IT READS AS A BROKEN SCREEN.
              It is usually a fact about the data: this subject has no marks in
              the current academic year and phase yet. The phone has said so for
              a while; the web left the select empty and silent. */}
          {!loading && selectedSubjectId && evaluations.length === 0 && (
            <Alert severity="info" sx={{ mb: 3 }}>
              {t('subjectReport.noEvaluations', {
                defaultValue: 'No marks recorded for this subject yet, so there is nothing to analyse.',
              })}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (!selectedSubjectId || !selectedEval) ? (
            <Alert severity="info">{t('subjectReportPage.messages.selectPerformanceFilters')}</Alert>
          ) : classPerformanceRows.length ? (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">{t('subjectReportPage.metrics.classOfferings')}</Typography>
                    <Typography variant="h5">{performanceSummary.classes}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">{t('subjectReportPage.metrics.studentsEvaluated')}</Typography>
                    <Typography variant="h5">{performanceSummary.students}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">{t('subjectReportPage.metrics.averageScore')}</Typography>
                    <Typography variant="h5">{formatNumber(performanceSummary.average)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">{t('subjectReportPage.metrics.successRate')}</Typography>
                    <Typography variant="h5">{formatPercent(performanceSummary.successRate)}</Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {selectedSubjectMeta?.subjectName || selectedSubjectMeta?.name || ''}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {`${buildEvaluationLabel(selectedEvaluationMeta)} • ${t('subjectReportPage.messages.performanceRateHelper', { count: classes.length, passAverage: formatNumber(passAverage) })}`}
                </Typography>
                <Box sx={{ width: '100%', height: 320 }}>
                  <ResponsiveContainer>
                    <BarChart data={classPerformanceRows} margin={{ top: 8, right: 16, left: 0, bottom: 48 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="className" angle={-20} textAnchor="end" interval={0} height={64} />
                      <YAxis domain={[0, 100]} />
                      <Tooltip
                        formatter={(value, name) => {
                          if (name === t('subjectReportPage.metrics.successRate')) return [formatPercent(value), name];
                          return [value, name];
                        }}
                      />
                      <Bar dataKey="successRate" name={t('subjectReportPage.metrics.successRate')} fill="#1976d2" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>

              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('subjectReportPage.table.class')}</TableCell>
                      <TableCell align="right">{t('subjectReportPage.table.students')}</TableCell>
                      <TableCell align="right">{t('subjectReportPage.table.averageScore')}</TableCell>
                      <TableCell align="right">{t('subjectReportPage.table.minScore')}</TableCell>
                      <TableCell align="right">{t('subjectReportPage.table.maxScore')}</TableCell>
                      <TableCell align="right">{t('subjectReportPage.table.passed')}</TableCell>
                      <TableCell align="right">{t('subjectReportPage.table.successRate')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {classPerformanceRows.map((row) => (
                      <TableRow key={row.classId}>
                        <TableCell>{row.className}</TableCell>
                        <TableCell align="right">{row.students}</TableCell>
                        <TableCell align="right">{formatNumber(row.averageScore)}</TableCell>
                        <TableCell align="right">{formatNumber(row.minScore)}</TableCell>
                        <TableCell align="right">{formatNumber(row.maxScore)}</TableCell>
                        <TableCell align="right">{row.passed}</TableCell>
                        <TableCell align="right">{formatPercent(row.successRate)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Alert severity="info">{t('subjectReportPage.messages.noPerformanceRows')}</Alert>
          )}
        </>
      )}

      {!isPerformance && (
        <Box>
          {uploadsError && <Alert severity="error" sx={{ mb: 2 }}>{uploadsError}</Alert>}
          {activeDepartment?.id ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('subjectReportPage.messages.departmentUploadScope', { department: activeDepartment.name || t('common.department') })}
              {currentAcademicYear?.name ? ` ${currentAcademicYear.name}` : ''}
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>{t('subjectReportPage.messages.noDepartmentScope')}</Alert>
          )}

          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="report-month-select-label">{t('subjectReportPage.fields.month')}</InputLabel>
                  <Select
                    labelId="report-month-select-label"
                    value={selectedMonthOptionKey}
                    label={t('subjectReportPage.fields.month')}
                    onChange={(event) => {
                      const nextKey = String(event.target.value || '');
                      const matched = monthOptions.find((item) => item.key === nextKey);
                      if (matched) {
                        setUploadMonth(String(matched.month));
                        setUploadYear(String(matched.year));
                      }
                    }}
                  >
                    {monthOptions.map((item) => (
                      <MenuItem key={item.key} value={item.key}>{item.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="report-year-select-label">{t('subjectReportPage.fields.year')}</InputLabel>
                  <Select
                    labelId="report-year-select-label"
                    value={uploadYear}
                    label={t('subjectReportPage.fields.year')}
                    onChange={(event) => setUploadYear(String(event.target.value || ''))}
                  >
                    {yearOptions.map((year) => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button variant="outlined" component="label" fullWidth disabled={!activeDepartment?.id}>
                  {uploadFile ? uploadFile.name : t('subjectReportPage.actions.chooseFile')}
                  <input
                    type="file"
                    hidden
                    accept="application/pdf,image/png,image/jpeg"
                    onChange={(event) => setUploadFile(event.target.files?.[0] || null)}
                  />
                </Button>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button variant="contained" fullWidth onClick={handleUpload} disabled={!activeDepartment?.id || uploading}>
                  {uploading ? t('subjectReportPage.actions.uploading') : t('subjectReportPage.actions.upload')}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {uploadsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('subjectReportPage.table.period')}</TableCell>
                    <TableCell>{t('subjectReportPage.table.file')}</TableCell>
                    <TableCell>{t('subjectReportPage.table.type')}</TableCell>
                    <TableCell align="right">{t('subjectReportPage.table.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(uploads || []).map((upload) => (
                    <TableRow key={upload.id}>
                      <TableCell>{formatReportPeriod(upload.month, upload.year)}</TableCell>
                      <TableCell><a href={upload.fileUrl} target="_blank" rel="noreferrer">{upload.fileName}</a></TableCell>
                      <TableCell>{upload.fileType || ''}</TableCell>
                      <TableCell align="right">
                        <Button color="error" onClick={() => handleDeleteUpload(upload.id)}>{t('subjectReportPage.actions.delete')}</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!uploads || uploads.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">{t('subjectReportPage.messages.noUploads')}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );
}
